import React, { useState, useEffect } from 'react';
import { sendQuoteEmail } from './emailService';

const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    servicio: '',
    email: '',
    telefono: '',
    mensaje: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const servicios = [
    'Sitio Informativo',
    'E-commerce',
    'Software a la Medida',
    'Control Clientes'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.servicio) {
      newErrors.servicio = 'Debe seleccionar un servicio';
    }

    // At least one contact method required
    if (!formData.email.trim() && !formData.telefono.trim()) {
      newErrors.contacto = 'Debe proporcionar al menos un email o teléfono';
    }

    // Email validation if provided
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Send email using the email service
      const result = await sendQuoteEmail(formData);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      setSubmitStatus('success');
      setFormData({
        nombre: '',
        servicio: '',
        email: '',
        telefono: '',
        mensaje: ''
      });
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSubmitStatus('');
      }, 2000);

    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        width: '100vw',
        height: '100vh'
      }}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'white',
          borderRadius: '1.5rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '42rem',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <div 
          className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-3xl"
          style={{
            background: 'linear-gradient(to right, #2563eb, #9333ea)',
            padding: '1.5rem',
            borderRadius: '1.5rem 1.5rem 0 0',
          }}
        >
          <div 
            className="flex justify-between items-center"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div>
              <h2 
                className="text-2xl font-bold text-white"
                style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: 'white'
                }}
              >
                Solicita una Cotización
              </h2>
              <p 
                className="text-blue-100 mt-1"
                style={{
                  color: '#dbeafe',
                  marginTop: '0.25rem'
                }}
              >
                Cuéntanos sobre tu proyecto y te contactaremos pronto
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.5rem',
                padding: '0.5rem'
              }}
            >
              <i className="fa-solid fa-times text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Form */}
        <div 
          className="p-6"
          style={{
            padding: '1.5rem'
          }}
        >
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center">
                <i className="fa-solid fa-check-circle text-green-600 text-xl mr-3"></i>
                <p className="text-green-800 font-medium">¡Cotización enviada exitosamente! Te contactaremos pronto.</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center">
                <i className="fa-solid fa-exclamation-circle text-red-600 text-xl mr-3"></i>
                <p className="text-red-800 font-medium">Error al enviar la cotización. Inténtalo de nuevo.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                  errors.nombre ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-blue-500'
                }`}
                placeholder="Tu nombre completo"
              />
              {errors.nombre && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <i className="fa-solid fa-exclamation-circle mr-1"></i>
                  {errors.nombre}
                </p>
              )}
            </div>

            {/* Servicio */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Servicio <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {servicios.map((servicio) => (
                  <label key={servicio} className="flex items-center p-3 border-2 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                    <input
                      type="radio"
                      name="servicio"
                      value={servicio}
                      checked={formData.servicio === servicio}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                      formData.servicio === servicio 
                        ? 'border-blue-600 bg-blue-600' 
                        : 'border-slate-300'
                    }`}>
                      {formData.servicio === servicio && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span className="text-sm font-medium text-slate-700">{servicio}</span>
                  </label>
                ))}
              </div>
              {errors.servicio && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <i className="fa-solid fa-exclamation-circle mr-1"></i>
                  {errors.servicio}
                </p>
              )}
            </div>

            {/* Contact Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    errors.email ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-blue-500'
                  }`}
                  placeholder="tu@email.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <i className="fa-solid fa-exclamation-circle mr-1"></i>
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    errors.contacto ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-blue-500'
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            {errors.contacto && (
              <p className="text-red-500 text-sm flex items-center">
                <i className="fa-solid fa-exclamation-circle mr-1"></i>
                {errors.contacto}
              </p>
            )}

            {/* Mensaje */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Mensaje adicional
              </label>
              <textarea
                name="mensaje"
                value={formData.mensaje}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                placeholder="Cuéntanos más detalles sobre tu proyecto..."
              />
            </div>

            {/* Submit Button */}
            <div 
              className="flex gap-4 pt-4"
              style={{
                display: 'flex',
                gap: '1rem',
                paddingTop: '1rem'
              }}
            >
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-semibold"
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #cbd5e1',
                  color: '#334155',
                  background: 'white',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                style={{
                  flex: 1,
                  background: 'linear-gradient(to right, #2563eb, #9333ea)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.75rem',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    Enviando...
                  </span>
                ) : (
                  'Enviar Cotización'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
